import { useCallback } from 'react';

import { useUserSettings } from '#settingsFeature/models/userSettings/queries';

import { useIntegrationsList } from '../../models/integrations/queries';
import { useAircallEverywhereStore } from '../../stores/aircallEverywhereStore';

/**
 * Determines calling mode based on user settings and Aircall connection.
 * @returns {'everywhere' | 'desktop' | 'tel'}
 */
export function useCallingMode() {
	const { data: settings } = useUserSettings();
	const { data: integrations = [] } = useIntegrationsList();

	const isAircallConnected = integrations.find((i) => i.slug === 'phone_aircall')?.isConnected ?? false;
	const everywhereEnabled = settings?.userSettings?.aircallEverywhereEnabled === true;

	if (isAircallConnected && everywhereEnabled) return 'everywhere';
	if (isAircallConnected) return 'desktop';
	return 'tel';
}

/**
 * Hook to interact with Aircall Everywhere SDK.
 * Provides dial(), call state, and SDK readiness info.
 */
export function useAircallEverywhere() {
	const phoneInstance = useAircallEverywhereStore((s) => s.phoneInstance);
	const isLoggedIn = useAircallEverywhereStore((s) => s.isLoggedIn);
	const sdkReady = useAircallEverywhereStore((s) => s.sdkReady);
	const callState = useAircallEverywhereStore((s) => s.callState);
	const activeCall = useAircallEverywhereStore((s) => s.activeCall);

	const dial = useCallback(
		(phoneNumber) => {
			if (!phoneInstance || !sdkReady || !isLoggedIn) {
				return Promise.reject(
					new Error(
						'Aircall Everywhere ist nicht bereit. Bitte stellen Sie sicher, dass Sie im Widget eingeloggt sind.',
					),
				);
			}

			return new Promise((resolve, reject) => {
				phoneInstance.send('dial_number', { phone_number: phoneNumber }, (success, data) => {
					if (success) resolve(data);
					else reject(new Error(data?.message ?? 'Anruf konnte nicht gestartet werden'));
				});
			});
		},
		[phoneInstance, sdkReady, isLoggedIn],
	);

	return { dial, isLoggedIn, sdkReady, callState, activeCall };
}
